import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardTitle,
  Flex,
  Icon,
  Text,
  type IIconProps,
} from '@kognitos/lattice';

import type { Bdkv2BookDescriptor } from '@/generated/hooks/bdk/models';
import { convertToDataUrl } from '@/shared/utils/image-utils';
import { getInitials, getIntegrationDisplayName } from '@/shared/utils';
import { hasIntegrationTriggers } from '../utils';

interface IIntegrationCardHeaderProps {
  integration: Bdkv2BookDescriptor;
  /** Shown on the right, e.g. the connected badge. */
  badge?: ReactNode;
}

/**
 * The top row of an integration card: icon tile, name, capability line, and an
 * optional badge. Shared by the Integrations tab cards and the integration picker.
 */
export function IntegrationCardHeader({
  integration,
  badge,
}: IIntegrationCardHeaderProps) {
  const { t: tIntegrations } = useTranslation('integrations');
  const displayName = getIntegrationDisplayName(integration);
  const imageSrc = convertToDataUrl(integration.icon);
  const [imageError, setImageError] = useState(false);

  return (
    <Flex gap={2} align="center" justify="between" width="full">
      <Flex gap={3} align="center" flex="1" className="min-w-0">
        <Card className="size-11 shrink-0 items-center justify-center gap-0 rounded-md p-1.5">
          {imageSrc && !imageError ? (
            <img
              src={imageSrc}
              alt={displayName}
              className="size-10 object-contain"
              onError={() => setImageError(true)}
            />
          ) : (
            <Flex
              align="center"
              justify="center"
              className="size-10 bg-muted rounded"
            >
              <Text level="small" color="muted">
                {getInitials(displayName)}
              </Text>
            </Flex>
          )}
        </Card>
        <Flex direction="column" gap={1} flex="1" className="min-w-0">
          <CardTitle className="truncate w-full">{displayName}</CardTitle>
          <Flex gap={3} align="center" className="pb-1">
            <CapabilityLabel
              icon="Diamond"
              label={tIntegrations('integrations.integrationCard.action')}
            />
            {hasIntegrationTriggers(integration) && (
              <CapabilityLabel
                icon="Zap"
                label={tIntegrations('integrations.integrationCard.trigger')}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
      {badge}
    </Flex>
  );
}

interface ICapabilityLabelProps {
  icon: IIconProps['type'];
  label: string;
}

const CapabilityLabel = ({ icon, label }: ICapabilityLabelProps) => (
  <Flex gap={1} align="center">
    <Icon type={icon} size="xs" className="text-muted-foreground" />
    <Text level="xSmall" color="muted">
      {label}
    </Text>
  </Flex>
);
